import ReducerRegistry from '../base/redux/ReducerRegistry';
import { REQUEST_INTERPRETER, CANCEL_REQUEST, REQUEST_ACCEPTED, REQUEST_DECLINED, MATCH_FOUND, MEETING_INITIATED, INTERPRETED_CALL_ENDED, QUEUE_STATUS_UPDATE, QUEUE_CONNECTION_CHANGED, QUEUE_SERVICE_ERROR, INTERPRETER_AVAILABILITY_CHANGED, INTERPRETER_REQUEST_RECEIVED, INTERPRETER_WRAP_UP_UPDATED, SET_PAUSE_AFTER_CALL, QUEUE_CAPABILITIES_UPDATED, EXTERNAL_MEETING_CALL_UPDATED, EXTERNAL_MEETING_CALL_ENDED, MEETING_REMINDERS_SET, MEETING_REMINDERS_ADDED, MEETING_REMINDER_REMOVED } from './actionTypes';
const INITIAL_STATE = {
    isRequestPending: false,
    pendingRequests: [],
    isConnected: false,
    matchFound: false,
    activeInterpreters: 0
};
/**
 * Reducer function for the interpreter queue feature.
 *
 * @param {QueueState} state - The current Redux state.
 * @param {Object} action - The dispatched action.
 * @returns {QueueState}
 */
ReducerRegistry.register('features/interpreter-queue', (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case REQUEST_INTERPRETER:
            return {
                ...state,
                isRequestPending: true,
                currentRequestId: action.requestId,
                requestLanguage: action.language,
                requestExternalMeeting: action.externalMeeting,
                matchFound: false,
                matchData: undefined,
                queuePosition: undefined,
                lastError: undefined,
                requestNotice: undefined
            };
        case CANCEL_REQUEST:
            return {
                ...state,
                isRequestPending: false,
                currentRequestId: undefined,
                requestExternalMeeting: undefined,
                matchFound: false,
                matchData: undefined,
                queuePosition: undefined,
                lastError: undefined,
                requestNotice: action.notice
            };
        case QUEUE_SERVICE_ERROR:
            return {
                ...state,
                isRequestPending: false,
                currentRequestId: undefined,
                requestExternalMeeting: undefined,
                queuePosition: undefined,
                lastError: action.error || 'Unable to request an interpreter.'
            };
        case INTERPRETER_REQUEST_RECEIVED:
            if (!action.request) {
                return state;
            }
            return {
                ...state,
                pendingRequests: [
                    ...state.pendingRequests.filter(request => request.id !== action.request?.id),
                    {
                        callerIdPhone: action.request.callerIdPhone,
                        callType: action.request.callType,
                        corporateAccountId: action.request.corporateAccountId,
                        costCenter: action.request.costCenter,
                        department: action.request.department,
                        id: action.request.id,
                        clientName: action.request.clientName,
                        industryTag: action.request.industryTag,
                        instructions: action.request.instructions,
                        language: action.request.language,
                        offerId: action.request.offerId,
                        offerExpiresAt: action.request.offerExpiresAt,
                        requestKind: action.request.requestKind,
                        timestamp: action.request.timestamp || Date.now(),
                        roomName: action.request.roomName,
                        scheduledSessionId: action.request.scheduledSessionId,
                        scheduledStartAt: action.request.scheduledStartAt,
                        targetPhone: action.request.targetPhone,
                        targetPhoneLast4: action.request.targetPhoneLast4,
                        vcoEnabled: action.request.vcoEnabled,
                        externalMeeting: action.request.externalMeeting,
                        demoMode: action.request.demoMode === true
                    }
                ]
            };
        case QUEUE_CAPABILITIES_UPDATED:
            return { ...state, capabilities: action.capabilities || {} };
        case EXTERNAL_MEETING_CALL_UPDATED:
            if (!action.externalMeetingCall) {
                return state;
            }
            return {
                ...state,
                isRequestPending: false,
                currentRequestId: undefined,
                requestExternalMeeting: undefined,
                queuePosition: undefined,
                lastError: undefined,
                externalMeetingCall: action.externalMeetingCall
            };
        case MEETING_REMINDERS_SET:
            return { ...state, meetingReminders: action.reminders || [] };
        case MEETING_REMINDERS_ADDED: {
            const known = new Set((state.meetingReminders || []).map(reminder => reminder.id));
            return { ...state, meetingReminders: [...state.meetingReminders || [],
                    ...(action.reminders || []).filter(reminder => !known.has(reminder.id))] };
        }
        case MEETING_REMINDER_REMOVED:
            return { ...state, meetingReminders: (state.meetingReminders || []).filter(reminder => reminder.id !== action.reminderId) };
        case EXTERNAL_MEETING_CALL_ENDED:
            return state.externalMeetingCall && (!action.callId || action.callId === state.externalMeetingCall.callId)
                ? { ...state, externalMeetingCall: undefined }
                : state;
        case REQUEST_ACCEPTED:
            return {
                ...state,
                pendingRequests: state.pendingRequests.filter(r => r.id !== action.requestId)
            };
        case REQUEST_DECLINED:
            return {
                ...state,
                pendingRequests: state.pendingRequests.filter(r => r.id !== action.requestId)
            };
        case MATCH_FOUND:
            return {
                ...state,
                isRequestPending: false,
                matchFound: true,
                matchData: action.matchData,
                lastError: undefined,
                wrapUp: undefined
            };
        case MEETING_INITIATED:
            return {
                ...state,
                isRequestPending: false,
                matchFound: true,
                matchData: action.meetingData,
                wrapUp: undefined
            };
        case INTERPRETED_CALL_ENDED:
            // The wrap-up has already read the pause choice by now.
            return {
                ...state,
                isRequestPending: false,
                currentRequestId: undefined,
                matchFound: false,
                matchData: undefined,
                pauseAfterCall: false,
                queuePosition: undefined
            };
        case SET_PAUSE_AFTER_CALL:
            return {
                ...state,
                pauseAfterCall: Boolean(action.enabled)
            };
        case INTERPRETER_AVAILABILITY_CHANGED:
            return {
                ...state,
                interpreterAvailable: Boolean(action.available),
                interpreterAvailableSince: action.available && Number.isFinite(action.at) ? action.at : undefined
            };
        case INTERPRETER_WRAP_UP_UPDATED:
            return {
                ...state,
                wrapUp: action.wrapUp
            };
        case QUEUE_STATUS_UPDATE:
            if (!action.status) {
                return state;
            }
            return {
                ...state,
                currentRequestId: action.status.requestId || state.currentRequestId,
                activeInterpreters: typeof action.status.activeInterpreters === 'number'
                    ? action.status.activeInterpreters : state.activeInterpreters,
                pendingRequests: action.status.pendingRequests || state.pendingRequests,
                queueSize: typeof action.status.queueSize === 'number'
                    ? action.status.queueSize : state.queueSize,
                queuePosition: typeof action.status.queuePosition === 'number'
                    ? action.status.queuePosition
                    : state.queuePosition
            };
        case QUEUE_CONNECTION_CHANGED:
            return {
                ...state,
                // A dropped socket is out of the queue until it re-announces.
                interpreterAvailable: action.connected ? state.interpreterAvailable : false,
                interpreterAvailableSince: action.connected ? state.interpreterAvailableSince : undefined,
                isConnected: Boolean(action.connected),
                lastError: action.connected ? undefined : action.error || state.lastError
            };
        default:
            return state;
    }
});
